import CheckboxField from "components/controls/CheckboxField";
import ComboBoxField from "components/controls/ComboBoxField";
import DatePickerField from "components/controls/DatePickerField";
import DoubleNumberInputField from "components/controls/DoubleNumberInputField";
import NumberInputField from "components/controls/NumberInputField";
import TextInputField from "components/controls/TextInputField";
import { vehicleFormClasses } from "pages/vehicle/VehicleForm";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "store/hooks";
import {
  isDisabledExternal,
  isDisabledExternalOrHasBeenActivated,
} from "../../../helpers";
import useInsurerList from "./hooks/useInsurerList";

const FinanceTempMtpl = ({
  formikProps,
  userRole,
  route,
  vehicle,
  insurerList,
}) => {
  const { t } = useTranslation();
  const { setFieldValue, handleChange, values, errors } = formikProps;
  const taxonomy = useSelector((s) => s.taxonomy);
  const vehicleAppSettings = useSelector((s) => s.vehicle.vehicleAppSettings);

  const paymentMethodOptions = useMemo(() => {
    return taxonomy?.PaymentMethod?.items
      .filter((item) => vehicleAppSettings?.PAYMENT_METHODS.includes(item.code))
      .map((item) => ({
        value: item.id,
        label: t(item.code),
      }));
  }, [t, taxonomy?.PaymentMethod?.items, vehicleAppSettings]);

  const policyStatusOptions = useMemo(() => {
    return taxonomy?.VehiclePolicyStatusCode?.items.map((item) => ({
      value: item.id,
      label: t(item.code),
    }));
  }, [t, taxonomy?.VehiclePolicyStatusCode?.items]);

  const paymentMeanOptions = useMemo(() => {
    return taxonomy?.PremiumPaymentMean?.items.map((item) => ({
      value: item.id,
      label: t(item.code),
    }));
  }, [t, taxonomy?.PremiumPaymentMean?.items]);

  const insurerData = useInsurerList({
    insurerList,
    values,
    name: "mtplInsurerPartnerNumber",
    subName: "mtplInsurerBranchOffice",
    setFieldValue,
  });

  return (
    <>
      <div className={vehicleFormClasses.item}>
        <DatePickerField
          errors={errors}
          setFieldValue={setFieldValue}
          defaultValue={values.mtplLastStartDate}
          name={"mtplLastStartDate"}
          disabled={route === "readOnlyVehicle"}
          label={"bfm.vehicleForm.startDate.label"}
          placeholder={t("bfm.selectDate")}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "mtplLastStartDate"
          )}
          confidentiality={null}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <DatePickerField
          errors={errors}
          setFieldValue={setFieldValue}
          defaultValue={values.mtplImportEndDate}
          name={"mtplImportEndDate"}
          disabled={route === "readOnlyVehicle"}
          label={"bfm.vehicleForm.endDate.label"}
          placeholder={t("bfm.selectDate")}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "mtplImportEndDate"
          )}
          confidentiality={null}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <DatePickerField
          errors={errors}
          setFieldValue={setFieldValue}
          defaultValue={values.mtplPolicyCancelationDocumentDate}
          name={"mtplPolicyCancelationDocumentDate"}
          disabled={route === "readOnlyVehicle"}
          label={"bfm.vehicleForm.cancelationDocumentDate.label"}
          placeholder={t("bfm.selectDate")}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "mtplPolicyCancelationDocumentDate"
          )}
          confidentiality={null}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <ComboBoxField
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            route,
            vehicle,
            userRole,
          })}
          errors={errors}
          name="mtplInsurerPartnerNumber"
          label={"bfm.vehicleForm.mtplInsurerPartnerNumber.label"}
          setFieldValue={setFieldValue}
          options={insurerData.insurerOptions}
          defaultValue={values?.mtplInsurerPartnerNumber}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "mtplInsurerPartnerNumber"
          )}
          confidentiality={null}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <ComboBoxField
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            route,
            vehicle,
            userRole,
          })}
          errors={errors}
          name="mtplInsurerBranchOffice"
          label={"bfm.vehicleForm.insurerBranchOffice.label"}
          setFieldValue={setFieldValue}
          options={insurerData.insurerBranchOfficeOptions}
          defaultValue={values?.mtplInsurerBranchOffice}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "mtplInsurerBranchOffice"
          )}
          confidentiality={null}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <TextInputField
          value={values.mtplPolicyNumber}
          onChange={handleChange}
          errors={errors}
          name={"mtplPolicyNumber"}
          // multiline
          // rows={4}
          disabled={isDisabledExternal({ vehicleAppSettings, userRole, route })}
          label={"bfm.vehicleForm.policyNumber.label"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "mtplPolicyNumber"
          )}
        />
      </div>
      {vehicleAppSettings.DEFAULT_COLUMNS.includes("mtplGroupPolicyNumber") ? (
        <div className={vehicleFormClasses.item}>
          <TextInputField
            value={values.mtplGroupPolicyNumber}
            onChange={handleChange}
            errors={errors}
            name={"mtplGroupPolicyNumber"}
            // multiline
            // rows={4}
            disabled={isDisabledExternal({
              vehicleAppSettings,
              userRole,
              route,
            })}
            label={"bfm.mtplGroupPolicyNumber"}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "mtplGroupPolicyNumber"
            )}
          />
        </div>
      ) : null}
      {/* Put status in new line if comment is present (for hungarian fleets) */}
      {vehicleAppSettings?.DEFAULT_COLUMNS.includes(
        "mtplPolicyStatusComment"
      ) ? (
        <div className={vehicleFormClasses.item}>&nbsp;</div>
      ) : null}
      {vehicleAppSettings?.DEFAULT_COLUMNS.includes("mtplPolicyStatus") ? (
        <div className={vehicleFormClasses.item}>
          <ComboBoxField
            disabled={isDisabledExternal({
              vehicleAppSettings,
              userRole,
              route,
            })}
            errors={errors}
            name="mtplPolicyStatus"
            label={"bfm.vehicleForm.mtplPolicyStatus.label"}
            setFieldValue={setFieldValue}
            options={policyStatusOptions}
            defaultValue={values.mtplPolicyStatus}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "mtplPolicyStatus"
            )}
            confidentiality={null}
          />
        </div>
      ) : null}
      {vehicleAppSettings?.DEFAULT_COLUMNS.includes(
        "mtplPolicyStatusComment"
      ) ? (
        <div className={vehicleFormClasses.item}>
          <TextInputField
            value={values.mtplPolicyStatusComment}
            onChange={handleChange}
            errors={errors}
            name={"mtplPolicyStatusComment"}
            multiline
            rows={4}
            disabled={isDisabledExternal({
              vehicleAppSettings,
              userRole,
              route,
            })}
            label={"bfm.vehicleForm.mtplPolicyStatusComment.label"}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "mtplPolicyStatusComment"
            )}
          />
        </div>
      ) : null}
      <div className={vehicleFormClasses.item}>
        <ComboBoxField
          disabled={isDisabledExternal({ vehicleAppSettings, userRole, route })}
          errors={errors}
          name="mtplPaymentMethod"
          label={"bfm.vehicleForm.paymentMethod.label"}
          setFieldValue={setFieldValue}
          options={paymentMethodOptions}
          defaultValue={values.mtplPaymentMethod}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "mtplPaymentMethod"
          )}
          confidentiality={null}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <ComboBoxField
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            route,
            vehicle,
            userRole,
          })}
          errors={errors}
          name="paymentMean"
          label={"bfm.vehicleForm.paymentMean.label"}
          setFieldValue={setFieldValue}
          options={paymentMeanOptions}
          defaultValue={values.paymentMean}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "paymentMean"
          )}
          confidentiality={null}
        />
      </div>
      {vehicleAppSettings?.HAS_ANNUAL_PREMIUM_EUR ? (
        <div
          className={vehicleFormClasses.item}
          style={{ display: "flex", gap: "15px" }}
        >
          <div style={{ flex: 1 }}>
            <DoubleNumberInputField
              setFieldValue={setFieldValue}
              value={values?.mtplAnnualGrossPremium}
              onChange={handleChange}
              errors={errors}
              name={"mtplAnnualGrossPremium"}
              disabled={isDisabledExternalOrHasBeenActivated({
                vehicleAppSettings,
                route,
                vehicle,
                userRole,
              })}
              label={"bfm.vehicleForm.annualGrossPremium.label"}
              isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                "mtplAnnualGrossPremium"
              )}
            />
          </div>
          <div style={{ flex: 1 }}>
            <DoubleNumberInputField
              setFieldValue={setFieldValue}
              value={values?.mtplAnnualGrossPremiumEur}
              onChange={handleChange}
              errors={errors}
              name={"mtplAnnualGrossPremiumEur"}
              disabled={isDisabledExternalOrHasBeenActivated({
                vehicleAppSettings,
                route,
                vehicle,
                userRole,
              })}
              label={"bfm.vehicleForm.annualGrossPremiumEur.label"}
              isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                "mtplAnnualGrossPremiumEur"
              )}
            />
          </div>
        </div>
      ) : (
        <div className={vehicleFormClasses.item}>
          <DoubleNumberInputField
            setFieldValue={setFieldValue}
            value={values?.mtplAnnualGrossPremium}
            onChange={handleChange}
            errors={errors}
            name={"mtplAnnualGrossPremium"}
            disabled={isDisabledExternalOrHasBeenActivated({
              vehicleAppSettings,
              route,
              vehicle,
              userRole,
            })}
            label={"bfm.vehicleForm.annualGrossPremium.label"}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "mtplAnnualGrossPremium"
            )}
          />
        </div>
      )}
      {vehicleAppSettings?.HAS_ANNUAL_PREMIUM_EUR ? (
        <div
          className={vehicleFormClasses.item}
          style={{ display: "flex", gap: "15px" }}
        >
          <div style={{ flex: 1 }}>
            <DoubleNumberInputField
              setFieldValue={setFieldValue}
              value={values?.mtplAnnualNetPremium}
              onChange={handleChange}
              errors={errors}
              name={"mtplAnnualNetPremium"}
              disabled={isDisabledExternalOrHasBeenActivated({
                vehicleAppSettings,
                route,
                vehicle,
                userRole,
              })}
              label={"bfm.vehicleForm.annualNetPremium.label"}
              isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                "mtplAnnualNetPremium"
              )}
            />
          </div>
          <div style={{ flex: 1 }}>
            <DoubleNumberInputField
              setFieldValue={setFieldValue}
              value={values?.mtplAnnualNetPremiumEur}
              onChange={handleChange}
              errors={errors}
              name={"mtplAnnualNetPremiumEur"}
              disabled={isDisabledExternalOrHasBeenActivated({
                vehicleAppSettings,
                route,
                vehicle,
                userRole,
              })}
              label={"bfm.vehicleForm.annualNetPremiumEur.label"}
              isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                "mtplAnnualNetPremiumEur"
              )}
            />
          </div>
        </div>
      ) : (
        <div className={vehicleFormClasses.item}>
          <DoubleNumberInputField
            setFieldValue={setFieldValue}
            value={values?.mtplAnnualNetPremium}
            onChange={handleChange}
            errors={errors}
            name={"mtplAnnualNetPremium"}
            disabled={isDisabledExternalOrHasBeenActivated({
              vehicleAppSettings,
              route,
              vehicle,
              userRole,
            })}
            label={"bfm.vehicleForm.annualNetPremium.label"}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "mtplAnnualNetPremium"
            )}
          />
        </div>
      )}
      {vehicleAppSettings.SUSPENSION_DAYS_INCLUDED ? (
        <div className={vehicleFormClasses.item}>
          <NumberInputField
            setFieldValue={setFieldValue}
            value={values.mtplSuspensionDays}
            onChange={handleChange}
            errors={errors}
            name={"mtplSuspensionDays"}
            disabled={isDisabledExternalOrHasBeenActivated({
              vehicleAppSettings,
              route,
              vehicle,
              userRole,
            })}
            label={"bfm.vehicleForm.suspensionDays.label"}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "mtplSuspensionDays"
            )}
          />
        </div>
      ) : null}
      {vehicleAppSettings.ACTIVATION_DAYS_INCLUDED ? (
        <div className={vehicleFormClasses.item}>
          <NumberInputField
            setFieldValue={setFieldValue}
            value={values.mtplActivationDays}
            onChange={handleChange}
            errors={errors}
            name={"mtplActivationDays"}
            disabled={isDisabledExternalOrHasBeenActivated({
              vehicleAppSettings,
              route,
              vehicle,
              userRole,
            })}
            label={"bfm.vehicleForm.mtplActivationDays.label"}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "mtplActivationDays"
            )}
          />
        </div>
      ) : null}
      {vehicleAppSettings.SUSPENSION_DAYS_INCLUDED ? (
        <div className={vehicleFormClasses.item}>
          <NumberInputField
            setFieldValue={setFieldValue}
            value={values.mtplSuspensionDaysLimit}
            onChange={handleChange}
            errors={errors}
            name={"mtplSuspensionDaysLimit"}
            disabled={isDisabledExternalOrHasBeenActivated({
              vehicleAppSettings,
              userRole,
              route,
              vehicle,
            })}
            label={"bfm.vehicleForm.mtplSuspensionDaysLimit.label"}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "mtplSuspensionDaysLimit"
            )}
            isNegativeAllowed={true}
          />
        </div>
      ) : null}
      <div className={vehicleFormClasses.item}>
        <NumberInputField
          setFieldValue={setFieldValue}
          value={values.mtplDueMonth}
          onChange={handleChange}
          errors={errors}
          inputProps={{ type: "number", max: "12" }}
          name={"mtplDueMonth"}
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            route,
            vehicle,
            userRole,
          })}
          label={"bfm.vehicleForm.mtplDueMonth.label"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "mtplDueMonth"
          )}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <NumberInputField
          setFieldValue={setFieldValue}
          value={values.mtplDueDay}
          onChange={handleChange}
          errors={errors}
          name={"mtplDueDay"}
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            route,
            vehicle,
            userRole,
          })}
          label={"bfm.vehicleForm.mtplDueDay.label"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "mtplDueDay"
          )}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <DatePickerField
          errors={errors}
          setFieldValue={setFieldValue}
          defaultValue={values.mtplPolicyDocumentDate}
          name={"mtplPolicyDocumentDate"}
          disabled={route === "readOnlyVehicle"}
          label={"bfm.vehicleForm.documentDate.label"}
          placeholder={t("bfm.selectDate")}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "mtplPolicyDocumentDate"
          )}
          confidentiality={null}
        />
      </div>
      {vehicleAppSettings.HAS_GREENCARD_EXPIRY_DATE ? (
        <div className={vehicleFormClasses.item}>
          <DatePickerField
            errors={errors}
            setFieldValue={setFieldValue}
            defaultValue={values.mtplGreenCardExpiryDate}
            name={"mtplGreenCardExpiryDate"}
            disabled={route === "readOnlyVehicle"}
            label={"bfm.fleetForm.mtplGreenCardExpiryDate.label"}
            placeholder={t("bfm.selectDate")}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "mtplGreenCardExpiryDate"
            )}
            confidentiality={null}
          />
        </div>
      ) : null}
      <div className={vehicleFormClasses.fullWidthFormItem}>
        <CheckboxField
          checked={values?.mtplIsPolicyIssued}
          errors={errors}
          setFieldValue={setFieldValue}
          name={"mtplIsPolicyIssued"}
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            userRole,
            route,
            vehicle,
          })}
          label={"bfm.mtplIsPolicyIssued.label"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "mtplIsPolicyIssued"
          )}
        />
      </div>
      <div className={vehicleFormClasses.fullWidthFormItem}>
        <TextInputField
          value={values.mtplComment}
          onChange={handleChange}
          errors={errors}
          name={"mtplComment"}
          multiline
          rows={4}
          disabled={isDisabledExternal({ vehicleAppSettings, userRole, route })}
          label={"bfm.vehicleForm.mtplComment.label"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "mtplComment"
          )}
        />
      </div>
    </>
  );
};

export default FinanceTempMtpl;
